import { useTranslation } from 'next-i18next'
import { Fragment, useEffect, useRef, useState } from 'react'

import LeaderboardItem from '~/Leaderboard/components/LeaderboardItem'
import { LeaderboardData, LeaderboardUser } from '~/Leaderboard/types/leaderboard.types'
import { IUser } from '~/types/user-info-types'
import Unranked from '~/Leaderboard/components/Unranked'
import PromotionLine from '~/Leaderboard/components/PromotionLine'
import DemoteLine from '~/Leaderboard/components/DemoteLine'

export default function CurrentLeaderboard({
  animate,
  leaderboardData,
  user,
}: {
  animate?: { from: number; to: number }
  leaderboardData?: LeaderboardData
  user: IUser | null
}): React.ReactElement {
  const { t } = useTranslation(['leaderboard', 'homebase'])
  const [startAnimation, setStartAnimation] = useState(false)
  const currentUserItemRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)
  const [LeaderBoardList, setLeaderBoardList] = useState<LeaderboardUser[] | []>(
    leaderboardData ? leaderboardData.users : [],
  )
  const isCrossingPromotionLine =
    (animate &&
      leaderboardData?.promotionSettings?.promote &&
      animate.from > leaderboardData.promotionSettings.promote &&
      animate.to <= leaderboardData.promotionSettings.promote) ||
    false
  const demotionLinePos =
    (leaderboardData?.leaderboardCapacity ?? 0) - (leaderboardData?.promotionSettings?.demote ?? 0)
  const isCrossingDemotionLine =
    (animate &&
      leaderboardData?.promotionSettings?.demote &&
      animate.from > demotionLinePos &&
      animate.to <= demotionLinePos) ||
    false

  // Only animate a list that has the user parked at the start rank. On any other list the moves below
  // slide rows onto each other and the final reorder never runs to clear them.
  const canAnimate = !!animate && animate.from > animate.to && LeaderBoardList[animate.from - 1]?.id === user?.id

  const numberOfSpacees = animate ? animate?.from - animate?.to : 0
  const numberOfLinesCrossed =
    isCrossingPromotionLine && isCrossingDemotionLine ? 2 : isCrossingPromotionLine || isCrossingDemotionLine ? 1 : 0

  useEffect(() => {
    // making sure the current user is in the middle of the screen when we start the animation
    if (currentUserItemRef.current) {
      currentUserItemRef.current.scrollIntoView({
        block: 'center',
      })
    }
    const startAnimTimeout = setTimeout(() => {
      setStartAnimation(true)
      // Scrolling to the position the user will be moved to after the animation
      if (targetRef.current) {
        targetRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
    }, 1000)

    const endAnimTimeout = setTimeout(() => {
      if (currentUserItemRef.current) {
        // make sure the current user is in the middle of the screen after the animation is done
        currentUserItemRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      }
      updateLeaderboardList()
    }, 2000)
    return () => {
      clearTimeout(startAnimTimeout)
      clearTimeout(endAnimTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateLeaderboardList = (): void => {
    if (animate && canAnimate) {
      // Update the leaderboard list so the XP and position number in the list data is correct after the animation is done
      setLeaderBoardList(() => {
        const newList = [...(leaderboardData?.users || [])]
        const userToMove = newList[animate.from - 1] // Adjust for zero-based index

        // only mutating a copy of the array
        // eslint-disable-next-line fp/no-mutating-methods
        newList.splice(animate.from - 1, 1)
        // eslint-disable-next-line fp/no-mutating-methods
        newList.splice(animate.to - 1, 0, userToMove)
        setStartAnimation(false)
        return newList
      })
    }
  }

  if (!LeaderBoardList[0] && animate) return <Fragment />
  return (
    <Fragment>
      {LeaderBoardList.length ? (
        <Fragment>
          <div className="text-md px-3">
            <div className="pt-4 lg:px-4">
              <div className="flex items-center justify-evenly pt-3 pb-1 sm:gap-2 border-t-2 border-white">
                <p className="w-16 text-center">{t('leaderboard:rank')}</p>
                <p className="flex-auto ml-10 sm:ml-12">{t('leaderboard:nickname')}</p>
                <p className="w-30 text-center">{t('homebase:xp')}</p>
              </div>
            </div>
            <div className={animate ? 'max-h-96 overflow-y-auto hide-scrollbar' : ''}>
              <div className="flex flex-col relative gap-2 h-full min-h-content">
                {leaderboardData &&
                  LeaderBoardList.map((item, index) => (
                    <div key={item.id}>
                      <PromotionLine index={index} leaderboardData={leaderboardData} />
                      <DemoteLine index={index} leaderboardData={leaderboardData} />
                      <div
                        className={`relative ${startAnimation ? 'duration-1000' : ''} ${
                          item.id === user?.id ? 'z-20' : 'z-10'
                        }`}
                        ref={
                          item.id === user?.id ? currentUserItemRef : index + 1 === animate?.to ? targetRef : undefined
                        }
                        style={
                          animate && startAnimation && canAnimate
                            ? // Current user animation --- start ---
                              item.id === user?.id
                              ? // Check if we are at the starting position
                                index + 1 === animate.from
                                ? {
                                    // Animate the user to the new position taking into account the number of spaces and lines crossed
                                    transform: `translateY(calc(-${numberOfSpacees * 100}% - ${
                                      numberOfSpacees * 8
                                    }px - ${numberOfLinesCrossed * 110}px))`,
                                  }
                                : {}
                              : // Current user animation --- end ---
                              // Other users animations --- start ---
                              // check if it is between the starting and ending position
                              index + 1 < animate.from && index + 1 >= animate.to
                              ? // check if the promotion line is crossed by this list item
                                (index + 1 === leaderboardData.promotionSettings.promote &&
                                  leaderboardData.promotionSettings.promote >= animate.to) ||
                                // also check if the demotion line is crossed by this list item
                                (index + 1 === demotionLinePos && demotionLinePos >= animate.to)
                                ? {
                                    // crossing a line
                                    transform: `translateY(calc(100% + 8px + 110px))`,
                                  }
                                : {
                                    // just moving one space
                                    transform: `translateY(calc(100% + 8px))`,
                                  }
                              : {}
                            : // Other users animations --- end ---
                              {}
                        }
                      >
                        <LeaderboardItem
                          item={item}
                          leaderboardCapacity={leaderboardData.leaderboardCapacity}
                          promotionSettings={leaderboardData.promotionSettings}
                          rank={index + 1}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </Fragment>
      ) : (
        <Unranked user={user} />
      )}
    </Fragment>
  )
}
